/**
 * Readable shop product URLs (Warren, 4 November 2026): /product/<slug> in place of /product/<database id>. The slug is
 * stored on the product (Product.slug, Product.previous_slugs), and nothing derives it at read time, so a published URL
 * never changes because a name did. Pure, so the model, the catalog seed, the scripts, the Api and both storefronts share
 * one rule.
 */
const SLUG_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const DATABASE_ID_SHAPE = /^[a-f0-9]{24}$/;
/** Lowercase letters, digits and single hyphens, and never the shape of a database id, so a path segment can only mean one
 *  of the two. */
export function isProductSlug(value) {
    return typeof value === 'string' && SLUG_SHAPE.test(value) && !DATABASE_ID_SHAPE.test(value);
}
/** Whether a path segment has the shape of a database id. */
export const isProductDatabaseId = (value) => DATABASE_ID_SHAPE.test(value);
const DROPPED_WORDS = new Set(['mount', 'cable', 'cables']);
/** The rule the catalog's slugs were made by: lowercase, drop parentheticals, non-alphanumerics to hyphens, drop the words
 *  mount, cable and cables. A record of the rule, not a source of URLs: the slugs themselves are stored. */
export function deriveProductSlug(name) {
    return name.toLowerCase().replace(/\([^)]*\)/g, ' ').split(/[^a-z0-9]+/).filter(w => w && !DROPPED_WORDS.has(w)).join('-');
}
/** A product's page on the shop. Falls back to the id for a row not yet backfilled, which is its canonical URL until then. */
export function productPath(p) {
    return `/product/${p.slug || p.id}`;
}
/**
 * Every slug held more than once across these rows, as one line each. The unique indexes catch a slug held twice in one
 * field; only this catches one row's slug being another row's previous slug, which would send an old URL to the wrong
 * product. A row's own slug in its own previous_slugs, and a repeat within previous_slugs, count too. Empty: no clash.
 */
export function productSlugClashes(rows) {
    const holders = new Map();
    const hold = (slug, who) => holders.set(slug, [...(holders.get(slug) ?? []), who]);
    for (const r of rows) {
        if (r.slug)
            hold(r.slug, `${r.part_number} slug`);
        for (const old of r.previous_slugs ?? [])
            hold(old, `${r.part_number} previous slug`);
    }
    return [...holders].filter(([, who]) => who.length > 1).map(([slug, who]) => `${slug} is held by ${who.join(' and ')}`);
}
//# sourceMappingURL=productSlug.js.map